import { createApi } from '@reduxjs/toolkit/query/react';
import { DateTimeString } from '../../models/reports.external';
import { VirtualConversationType } from '../../models/reports.internal';
import { createRbacBaseQuery } from '../../utils/helpers';

interface ODataResult<T> {
  '@odata.count': number;
  'value': T[];
}

export interface HDQueueSegment {
  id: string;
  queueId: string;
  conversationId: string;
  queuedAtUtc: DateTimeString;
  dequeuedAtUtc: DateTimeString;
  dequeuedReason: string;
  durationInSeconds: number;
  conversation: {
    customerFirstName: string;
    customerMiddleName: string;
    customerLastName: string;
    customerEmailAddress: string | null;
    customerPhoneNumber: string | null;
  }
}

export interface HDAgentSegment {
  id: string;
  conversationId: string;
  createdAtUtc: DateTimeString;
  closedAtUtc: DateTimeString;
  durationInSeconds: number;
  conversation: {
    startedAt: DateTimeString;
    endedAt: DateTimeString;
    durationInSeconds: number;
    customerFirstName: string;
    customerLastName: string;
    customerMiddleName: string;
    customerEmailAddress: string | null;
    customerPhoneNumber: string | null;
    virtualConversationType: VirtualConversationType | null;
  }
  queueSegment: {
    queuedAtUtc: DateTimeString;
    dequeuedAtUtc: DateTimeString;
    durationInSeconds: number;
  }
}

export interface HDMessageConsumption {
  id: string;
  channelId: string;
  createDate: string;
  agentMessages: number;
  customerMessages: number;
  systemMessages: number;
  channel: {
    name: string;
    channelType: string;
  }
}

interface ConversationQueryParams {
  queueId: string;
  from: string;
  skip: number;
  take: number;
  orderBy: string;
}

export type ChatConsumptionQueryParam = 'thisMonth' | 'lastMonth';

function monthToDateFilter(month: ChatConsumptionQueryParam): string {
  const now = new Date();

  if (month === 'thisMonth') {
    const dateStart = new Date(now.getFullYear(), now.getMonth(), 0);
    const dateEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return `createDate ge Date(${dateStart.toISOString()}) and createDate lt Date(${dateEnd.toISOString()})`;
  }
  else {
    const dateStart = new Date(now.getFullYear(), now.getMonth() - 1, 0);
    const dateEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    return `createDate ge Date(${dateStart.toISOString()}) and createDate lt Date(${dateEnd.toISOString()})`;
  }
}

const historicalDataApi = createApi({
  reducerPath: 'historicalData',
  tagTypes: ['AbandonedConversations', 'CompletedConversations', 'MessageConsumptions'],
  baseQuery: createRbacBaseQuery(''),
  endpoints: (builder => ({
    getAbandonedConversations: builder.query<{ total: number, queueSegments: HDQueueSegment[] }, ConversationQueryParams>({
      query: ({ queueId, from, skip, take, orderBy }) => `odata/v1/queueSegments?$count=true&$orderBy=${orderBy}&$skip=${skip}&$top=${take}&$filter=dequeuedReason eq 'CustomerDisconnected' and queueId eq '${queueId}' and queuedAtUtc gt Date(${from}) and durationInSeconds gt abandonedThresholdInSeconds&$expand=conversation($select=customerFirstName,customerMiddleName,customerLastName,customerEmailAddress,customerPhoneNumber)`,
      transformResponse: (result: ODataResult<HDQueueSegment>) => ({ total: result['@odata.count'], queueSegments: result.value }),
      providesTags: (_, __, { queueId, from, skip, take, orderBy }) => [{ type: 'AbandonedConversations', id: `${queueId}|${from}|${skip}|${take}|${orderBy}` }],
    }),
    getCompletedConversations: builder.query<{ total: number, agentSegments: HDAgentSegment[] }, ConversationQueryParams>({
      query: ({ queueId, from, skip, take, orderBy }) => `odata/v1/agentSegments?$count=true&$orderBy=${orderBy}&$skip=${skip}&$top=${take}&$filter=queueId eq '${queueId}' and createdAtUtc gt Date(${from})&$expand=conversation,queueSegment`,
      transformResponse: (result: ODataResult<HDAgentSegment>) => ({ total: result['@odata.count'], agentSegments: result.value }),
      providesTags: (_, __, { queueId, from, skip, take, orderBy }) => [{ type: 'CompletedConversations', id: `${queueId}|${from}|${skip}|${take}|${orderBy}` }],
    }),
    getMessageConsumptions: builder.query<HDMessageConsumption[], ChatConsumptionQueryParam>({
      query: (month) => `odata/v1/messageConsumptions?$filter=${monthToDateFilter(month)}&$expand=channel($select=name,channelType)`,
      providesTags: ['MessageConsumptions'],
      transformResponse: (result: ODataResult<HDMessageConsumption>) => result.value,
    }),
  })),
});

export default historicalDataApi;

export const {
  useGetAbandonedConversationsQuery,
  useGetCompletedConversationsQuery,
  useGetMessageConsumptionsQuery,
} = historicalDataApi;